import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	redirect,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, useActionData } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import {
	AppContainerGroup,
	AppContainerGroupColumn,
} from '#app/components/app-container.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { AuthAppLayout } from '#app/components/layout/app.js'
import { Spacer } from '#app/components/spacer.js'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { EmailSchema } from '#app/models/user._._schema'
import { requireAnonymous } from '#app/utils/auth.server.js'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import {
	respondWithError,
	respondWithSubmission,
} from '#app/utils/request-helpers.js'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return {}
}

const SimpleSignupSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const { prisma } = await import('#app/utils/db.server.ts')
	const formData = await request.formData()

	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SimpleSignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return respondWithSubmission(submission)
	}

	const { email } = submission.value

	// Check if already on waitlist
	const existingWaitlist = await prisma.userWaitlist.findUnique({
		where: { email },
	})

	let waitlistEntry
	if (existingWaitlist) {
		// Update existing waitlist entry timestamp
		waitlistEntry = await prisma.userWaitlist.update({
			where: { email },
			data: {
				updatedAt: new Date(),
				// Mark as simple signup type
				onboardingStatus: 'simple-waitlist',
			},
		})
	} else {
		// Create new waitlist entry with minimal data
		waitlistEntry = await prisma.userWaitlist.create({
			data: {
				email,
				onboardingStatus: 'simple-waitlist',
			},
		})
	}

	if (!waitlistEntry) {
		return respondWithError(submission, 'Failed to join waitlist')
	}

	// Redirect to waitlist confirmation page
	return redirect('/waitlist')
}

export const meta: MetaFunction = () => {
	return [{ title: 'Join Waitlist - Choros' }]
}

export default function SignupRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'simple-signup-form',
		constraint: getZodConstraint(SimpleSignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SimpleSignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<AuthAppLayout>
			<AppContainerGroup className="text-center">
				<h1 className="text-h1">Join the Waitlist</h1>
				<p className="mt-3 text-body-md text-muted-foreground">
					Be the first to know when Choros launches in your area.
				</p>
			</AppContainerGroup>
			<Spacer size="xs" />
			<AppContainerGroup>
				<AppContainerGroupColumn>
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<Field
							labelProps={{
								htmlFor: fields.email.id,
								children: 'Email Address',
							}}
							inputProps={{
								...getInputProps(fields.email, { type: 'email' }),
								autoFocus: true,
								autoComplete: 'email',
								placeholder: 'your@email.com',
							}}
							errors={fields.email.errors}
						/>

						<ErrorList errors={form.errors} id={form.errorId} />

						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Join Waitlist
						</StatusButton>
					</Form>
				</AppContainerGroupColumn>
			</AppContainerGroup>
		</AuthAppLayout>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
